import axios from "axios";
import { BASE_URL } from "../constants/constant.js";
import authService from "../services/auth.service.js";
import { startLoading, stopLoading } from "./loading-store";

export default function createAxiosClient() {
  const axiosService = axios.create({
    baseURL: BASE_URL,
  });

  // Request interceptor - add auth token to requests
  axiosService.interceptors.request.use(
    (config) => {
      if (!config.skipLoader) {
        startLoading(config.loaderMessage);
      }
      const token = authService.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      stopLoading();
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle 401 errors and token refresh
  axiosService.interceptors.response.use(
    (response) => {
      stopLoading();
      return response;
    },
    async function (error) {
      stopLoading();
      const originalRequest = error.config;

      // Account deactivated by admin — force logout immediately
      if (error.response?.status === 403 && error.response?.data?.error === 'Account deactivated') {
        authService.logout();
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // If 401 error and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to refresh token
          await authService.refreshTokens();
          
          // Retry original request with new token
          const token = authService.getIdToken();
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosService(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout and redirect
          authService.logout();
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return axiosService;
}

export function config() {
  const token = authService.getIdToken();

  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

export function isAuthenticated() {
  return !!(localStorage.getItem("usermame") || authService.isAuthenticated());
}

export function isPlanPurchased() {
  const planName   = localStorage.getItem("planName");
  const planExpiry = localStorage.getItem("planExpiry");
  if (!planName) return null;
  if (planExpiry) {
    const expiry = new Date(planExpiry);
    if (!isNaN(expiry) && expiry < new Date()) {
      // Plan expired — clear so UI updates correctly
      localStorage.removeItem("planName");
      localStorage.removeItem("planExpiry");
      localStorage.removeItem("planId");
      return null;
    }
  }
  return planName;
}


export function getPlanDaysLeft() {
  if (!isPlanPurchased()) return null;
  const planExpiry = localStorage.getItem("planExpiry");
  if (!planExpiry) return null;
  const expiry = new Date(planExpiry);
  if (isNaN(expiry)) return null;
  const days = Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
}

export function isChatting() {
  const chatUser = localStorage.getItem("chatUser");
  //   console.log(isPlanPurchased, "pp");

  if (!chatUser) return null;

  return JSON.parse(chatUser);
}
export function getUniqueRes(profileData) {
  let obj = {};
  for (const key in profileData) {
    if (profileData[key] != null) {
      obj[key] = profileData[key];
    }
  }
  return obj;
}

export function sanitizeProfileUpdate(profileData) {
  // Exclude null/undefined and empty-string values to avoid server validation errors
  const sanitized = getUniqueRes(profileData || {});
  Object.keys(sanitized).forEach(k => {
    if (typeof sanitized[k] === 'string' && sanitized[k].trim() === '') {
      delete sanitized[k];
    }
  });
  delete sanitized.updatedAt;
  delete sanitized.createdAt;
  delete sanitized.PK;
  delete sanitized.SK;
  delete sanitized.userId;
  
  // Convert ID fields to strings for DynamoDB compatibility (GSI keys must be strings)
  const idFields = [
    'stateId', 'cityId', 'religionId', 'casteId', 'gothraId', 'motherTongueId',
    'heightId', 'maritalStatusId', 'professionId', 'professionTypeId',
    'qualificationId', 'bloodGroupId', 'bodyTypeId', 'dietId',
    'healthInformationId', 'disabilityId', 'familyStatusId', 'familyValuesId',
    'fatherOccupationId', 'motherOccupationId', 'incomeId',
    'familyStateId', 'familyCityId'
  ];
  
  idFields.forEach(field => {
    if (sanitized[field] !== undefined && sanitized[field] !== null) {
      // convert to string only when a meaningful value exists
      sanitized[field] = String(sanitized[field]);
    }
  });
  
  return sanitized;
}

export function getMasterDataItem(dataArray, id) {
  if (!id || !dataArray || !Array.isArray(dataArray)) return null;
  const numId = parseInt(id);
  // Handle both 'id' and 'sid' field names from different API endpoints
  const item = dataArray.find(item => item.id === numId || item.sid === numId);
  return item ? { ...item, name: item.label || item.name } : null;
}
